import {
  mountReportSharing,
  type ReportAudienceDto,
  type ReportAudienceSave,
} from "./reportSharing.ts";
// RPTCAT0 shared fixed report frame. Hosts supply current authenticated requests, never cached authority.
import {
  CANNED_FAMILIES,
  cannedFamily,
  cannedLinkOnly,
  cannedRunBody,
  initialCannedFilters,
  isCannedCatalog,
  isCannedResult,
  originDto,
  record,
  reportWords,
  type CannedCatalogDto,
  type CannedCatalogEntry,
  type CannedFilters,
  type CannedOriginDto,
  type CannedResultDto,
} from "./cannedReportModel.ts";
import { reportNode as node, renderCannedResult } from "./cannedReportRender.ts";
import { usDate } from "./dateText.ts";
import { mountReportScheduleDialog, mountReportSchedules } from "./reportSchedules.ts";
import { hideHoverHelp, hoverHelp } from "./hoverHelp.ts";
import { saveBlob } from "./consoleDownload.ts";

export interface CannedReportHost {
  prefix: string;
  customPrefix: string;
  request<T>(
    method: "GET" | "POST",
    path: string,
    body?: unknown,
  ): Promise<{ status: number; body: T }>;
  file(
    path: string,
    body: unknown,
  ): Promise<{ status: number; blob?: Blob; filename?: string; message?: string }>;
  active(): boolean;
  locationId?: string;
  setAudience?(
    definitionId: string,
    audience: ReportAudienceSave,
  ): Promise<{ status: number; body: unknown }>;
}
export interface CannedFrame {
  destroy(): void;
  refresh(): Promise<void>;
}
function button(text: string, action: () => void, disabled = false): HTMLButtonElement {
  const b = node("button", text);
  b.type = "button";
  b.className = "cr-btn-quiet";
  b.disabled = disabled;
  b.onclick = action;
  return b;
}
function picker(
  label: string,
  values: readonly string[],
  current: string,
  change: (value: string) => void,
): HTMLLabelElement {
  const wrap = node("label", label),
    select = node("select");
  for (const value of values) select.append(new Option(reportWords(value) || "None", value));
  select.value = current;
  select.onchange = () => change(select.value);
  wrap.append(select);
  return wrap;
}
function message(reply: { status: number; body: unknown }): string {
  return record(reply.body) && typeof reply.body["message"] === "string"
    ? reply.body["message"]
    : reply.status === 409
      ? "Another report is running for this location. Try again."
      : reply.status === 401 || reply.status === 403
        ? "This report is no longer available to this account."
        : "Could not load this report. Try again.";
}
function scheduleHost(host: CannedReportHost, active: () => boolean, definitionId: string) {
  return {
    active,
    api: <T>(method: "GET" | "POST", suffix: string, body?: unknown) =>
      host.request<T & { message?: string }>(method, `${host.customPrefix}${suffix}`, body),
    ...(host.setAudience === undefined
      ? {}
      : {
          share: (target: HTMLElement, done: () => void) =>
            void shareCannedReport(target, host, definitionId, active, done),
        }),
  };
}
async function shareCannedReport(
  target: HTMLElement,
  host: CannedReportHost,
  definitionId: string,
  active: () => boolean,
  done: () => void,
): Promise<void> {
  if (!active() || host.setAudience === undefined) return;
  const status = node("p", "Loading sharing.");
  target.append(status);
  try {
    const reply = await host.request<unknown>(
      "GET",
      `${host.customPrefix}/saved/${encodeURIComponent(definitionId)}`,
    );
    if (!active()) {
      status.remove();
      return;
    }
    const audience = record(reply.body) ? reply.body["audience"] : undefined;
    if (
      reply.status !== 200 ||
      !record(reply.body) ||
      reply.body["canShare"] !== true ||
      !record(audience) ||
      !["private", "named", "legacy_shared", "legacy_network"].includes(String(audience["mode"])) ||
      !Number.isSafeInteger(audience["revision"]) ||
      !Array.isArray(audience["recipientIds"]) ||
      !audience["recipientIds"].every((id) => typeof id === "string")
    ) {
      status.textContent =
        reply.status === 200 ? "Sharing is unavailable for this report." : message(reply);
      return;
    }
    status.remove();
    mountReportSharing(target, {
      audience: audience as unknown as ReportAudienceDto,
      isCurrent: active,
      choices: async () => {
        const choices = await host.request<unknown>("GET", `${host.customPrefix}/audience-choices`);
        return choices.status === 200 &&
          record(choices.body) &&
          Array.isArray(choices.body["items"])
          ? { items: choices.body["items"] as Array<{ id: string; name: string }> }
          : { error: message(choices) };
      },
      save: async (input) => {
        if (!active()) return "This report is no longer open.";
        const saved = await host.setAudience!(definitionId, input);
        if (!active()) return "This report is no longer open.";
        return saved.status === 200 ? undefined : message(saved);
      },
      done: () => {
        if (active()) done();
      },
    });
  } catch {
    if (active()) status.textContent = "Could not load sharing. Try again.";
    else status.remove();
  }
}
export function mountCannedReports(target: HTMLElement, host: CannedReportHost): CannedFrame {
  let alive = true,
    generation = 0,
    catalog: CannedCatalogDto | undefined,
    entry: CannedCatalogEntry | undefined,
    applied: CannedFilters | undefined,
    draft: CannedFilters | undefined,
    editing = false,
    busy = false,
    error = "",
    mode: "table" | "chart" = "table",
    result: CannedResultDto | undefined,
    ranBody: Record<string, unknown> | undefined;
  let saved: { id: string; version: number } | undefined,
    requestId: string | undefined,
    saveTitle = "";
  let sessions: Array<{ id: string; label: string }> = [];
  let categoryRequestKey = "",
    categorySequence = 0,
    categoryLoading = false,
    categoryError = "",
    categoryValues: string[] = [];
  const root = node("section");
  root.className = "canned-frame";
  target.append(root);
  const active = () => alive && host.active() && root.isConnected;
  function draw() {
    if (!active()) return;
    hideHoverHelp();
    root.replaceChildren();
    if (catalog === undefined) {
      root.append(node("p", error || "Loading reports."));
      if (error) root.append(button("Try again", () => void refresh()));
      return;
    }
    const navigation = node("nav");
    navigation.className = "canned-navigation";
    navigation.setAttribute("aria-label", "Report catalog");
    for (const group of [...new Set(catalog.reports.map((r) => r.group))]) {
      navigation.append(node("h3", reportWords(group)));
      for (const report of catalog.reports.filter((r) => r.group === group)) {
        const choice = button(report.title, () => choose(report), busy);
        choice.setAttribute("aria-current", String(entry?.id === report.id));
        navigation.append(choice);
      }
    }
    const content = node("div");
    content.className = "canned-content";
    root.append(navigation, content);
    if (entry === undefined) {
      content.append(node("p", error || "No reports are currently available."));
      return;
    }
    const heading = node("div");
    heading.className = "canned-heading";
    heading.append(
      hoverHelp(
        node("h2", entry.title),
        "Recorded data from the selected report source.",
        "Only currently authorized locations and supported report controls are offered.",
        "Edit the period, then Apply.",
      ),
    );
    content.append(heading);
    if (!entry.canRun || !entry.template || !CANNED_FAMILIES[entry.id]) {
      content.append(
        node(
          "p",
          entry.unavailableReason
            ? reportWords(entry.unavailableReason)
            : "This report is not available yet.",
        ),
      );
      return;
    }
    if (applied === undefined) return;
    const toolbar = node("div");
    toolbar.className = "canned-toolbar";
    toolbar.append(
      node(
        "span",
        catalog.scope.kind === "fixed_location"
          ? catalog.scope.locations.map((i) => i.name).join(", ")
          : applied.allLocations
            ? "All authorized locations"
            : catalog.scope.locations
                .filter((i) => applied?.locationIds.includes(i.id))
                .map((i) => i.name)
                .join(", "),
      ),
    );
    if (entry.id !== "current-inventory-value")
      toolbar.append(
        node(
          "span",
          applied.preset === "custom"
            ? `${usDate(applied.from)} to ${usDate(applied.to)}`
            : reportWords(applied.preset),
        ),
      );
    if (typeof applied.category === "string") toolbar.append(node("span", applied.category));
    if (["current-open-purchase-orders", "network-current-open-purchase-orders"].includes(entry.id))
      toolbar.append(node("span", "Creation-date cohort · current open status"));
    if (entry.id === "received-spend-by-vendor")
      toolbar.append(node("span", "Received in period · corrections as of read"));
    if (entry.id === "recorded-transfer-quantities" && applied.direction)
      toolbar.append(node("span", reportWords(applied.direction)));
    toolbar.append(
      button(
        "Edit",
        () => {
          draft = structuredClone(applied);
          categoryRequestKey = "";
          categorySequence++;
          editing = true;
          error = "";
          draw();
        },
        busy || editing,
      ),
    );
    content.append(toolbar);
    if (editing && draft !== undefined) content.append(editor());
    if (error) {
      const status = node("p", error);
      status.setAttribute("role", "alert");
      content.append(
        status,
        button("Try again", () => void run(), busy),
      );
    }
    const actions = node("div");
    actions.className = "canned-actions";

    actions.append(
      button("Run", () => void run(), busy || editing),
      button(
        "Table",
        () => {
          mode = "table";
          draw();
        },
        busy,
      ),
      button(
        "Chart",
        () => {
          mode = "chart";
          draw();
        },
        busy || !result || !chartAvailable(result),
      ),
    );
    actions.append(
      button(
        "CSV",
        () => void download(),
        busy || editing || !result || !ranBody || !entry.canExport,
      ),
    );
    if (entry.canSaveDerivative) {
      const name = node("input");
      name.type = "text";
      name.maxLength = 120;
      name.value = saveTitle;
      name.placeholder = "Saved report name";
      name.setAttribute("aria-label", "Saved report name");
      name.disabled = busy;
      name.oninput = () => {
        saveTitle = name.value;
      };
      actions.append(
        name,
        button(
          saved ? "Saved" : "Save report",
          () => void save(false),
          busy || editing || !result || saved !== undefined,
        ),
      );
    }
    if (saved && host.setAudience)
      actions.append(
        button(
          "Sharing",
          () => {
            const id = saved!.id,
              captured = generation;
            void shareCannedReport(
              root,
              host,
              id,
              () => active() && !editing && generation === captured && saved?.id === id,
              draw,
            );
          },
          busy || editing,
        ),
      );
    if (entry.canSchedule)
      actions.append(button("Schedule", () => void save(true), busy || editing || !result));
    content.append(actions);
    if (busy) {
      const status = node("p", "Loading.");
      status.setAttribute("role", "status");
      content.append(status);
    }
    if (result) content.append(renderCannedResult(result, mode));
  }
  function choose(next: CannedCatalogEntry) {
    if (busy) return;
    generation++;
    categorySequence++;
    categoryRequestKey = "";
    categoryValues = [];
    categoryError = "";
    categoryLoading = false;
    entry = next;
    applied = initialCannedFilters(next, catalog!);
    draft = undefined;
    editing = false;
    result = undefined;
    ranBody = undefined;
    saved = undefined;
    requestId = undefined;
    saveTitle = next.title;
    error = "";
    mode = "table";
    sessions = [];
    draw();
  }
  function categoryBodyKey(): { body: Record<string, unknown>; key: string } | undefined {
    if (!entry?.selectedCategoryTemplate || !entry.categoryOptionsPath || !catalog || !draft)
      return undefined;
    const body = cannedRunBody(entry, { ...draft, category: null }, catalog);
    return { body, key: JSON.stringify([entry.id, body]) };
  }
  async function loadCategoryChoices() {
    if (!active() || !editing || !entry || !draft) return;
    let request: ReturnType<typeof categoryBodyKey>;
    try {
      request = categoryBodyKey();
    } catch {
      const invalidMessage = "Choose valid dates and locations for categories.";
      const changed =
        categoryRequestKey !== "" ||
        categoryLoading ||
        categoryValues.length > 0 ||
        categoryError !== invalidMessage;
      if (changed) {
        categorySequence++;
        categoryRequestKey = "";
        categoryLoading = false;
        categoryValues = [];
        categoryError = invalidMessage;
        draw();
      }
      return;
    }
    if (!request || request.key === categoryRequestKey) return;
    const selectedEntry = entry,
      selectedDraft = draft,
      captured = generation,
      sequence = ++categorySequence;
    categoryRequestKey = request.key;
    categoryLoading = true;
    categoryValues = [];
    categoryError = "";
    draw();
    const current = () =>
      active() &&
      editing &&
      draft === selectedDraft &&
      entry === selectedEntry &&
      captured === generation &&
      sequence === categorySequence &&
      (() => {
        try {
          return categoryBodyKey()?.key === request!.key;
        } catch {
          return false;
        }
      })();
    try {
      if (selectedEntry.categoryOptionsPath !== `${selectedEntry.id}/category-options`)
        throw new Error("Category choices are unavailable.");
      const reply = await host.request<unknown>(
        "POST",
        `${host.prefix}/${selectedEntry.categoryOptionsPath}`,
        request.body,
      );
      if (!current()) return;
      if (
        reply.status !== 200 ||
        !record(reply.body) ||
        !Array.isArray(reply.body["categories"]) ||
        !reply.body["categories"].every((value) => typeof value === "string")
      ) {
        categoryError = reply.status === 200 ? "Category choices are unavailable." : message(reply);
      } else categoryValues = reply.body["categories"] as string[];
    } catch {
      if (current()) categoryError = "Category choices are unavailable.";
    } finally {
      if (current()) {
        categoryLoading = false;
        draw();
      }
    }
  }
  function editor(): HTMLElement {
    const form = node("form");
    form.className = "canned-filters";
    const draftValue = draft!,
      selectedEntry = entry!,
      scope = catalog!.scope;
    if (selectedEntry.selectedCategoryTemplate && selectedEntry.categoryOptionsPath) {
      queueMicrotask(() => {
        void loadCategoryChoices();
      });
    }
    if (scope.kind === "network") {
      const sites = node("fieldset");
      sites.append(node("legend", "Locations"));
      if (selectedEntry.maxSelectedLocations !== 1) {
        const all = node("input");
        all.type = "checkbox";
        all.checked = draftValue.allLocations;
        all.onchange = () => {
          draftValue.allLocations = all.checked;
          sessions = [];
          draw();
        };
        const label = node("label", "All authorized locations");
        label.prepend(all);
        sites.append(label);
      }
      for (const site of scope.locations) {
        const choice = node("input");
        choice.type = "checkbox";
        choice.checked = draftValue.allLocations || draftValue.locationIds.includes(site.id);
        choice.disabled = draftValue.allLocations;
        choice.onchange = () => {
          draftValue.locationIds =
            selectedEntry.maxSelectedLocations === 1
              ? choice.checked
                ? [site.id]
                : []
              : choice.checked
                ? [...draftValue.locationIds, site.id]
                : draftValue.locationIds.filter((id) => id !== site.id);
          draftValue.countSessionId = "";
          draftValue.baselineCountSessionId = "";
          draftValue.comparisonCountSessionId = "";
          sessions = [];
          draw();
        };
        const label = node(
          "label",
          `${site.name}${site.franchisee ? ` · ${site.franchisee}` : ""}`,
        );
        label.prepend(choice);
        sites.append(label);
      }
      form.append(sites);
    }
    if (selectedEntry.id === "recorded-transfer-quantities")
      form.append(
        picker("Direction", selectedEntry.directions ?? [], draftValue.direction ?? "", (value) => {
          if (value === "incoming" || value === "outgoing") draftValue.direction = value;
        }),
      );
    if (selectedEntry.id !== "current-inventory-value") {
      form.append(
        picker(
          ["current-open-purchase-orders", "network-current-open-purchase-orders"].includes(
            selectedEntry.id,
          )
            ? "Created in period"
            : "Period",
          [...(selectedEntry.datePresets ?? []), "custom"],
          draftValue.preset,
          (v) => {
            draftValue.preset = v;
            sessions = [];
            draw();
          },
        ),
      );
      if (draftValue.preset === "custom")
        for (const key of ["from", "to"] as const) {
          const label = node("label", key === "from" ? "From" : "To"),
            input = node("input");
          input.type = "date";
          input.value = draftValue[key];
          input.max = scope.businessDate;
          input.required = true;
          input.onchange = () => {
            draftValue[key] = input.value;
            sessions = [];
            if (selectedEntry.selectedCategoryTemplate) draw();
          };
          label.append(input);
          form.append(label);
        }
      if (selectedEntry.maxRangeDays)
        form.append(node("span", `Up to ${selectedEntry.maxRangeDays} days`));
    }
    if ((selectedEntry.compareKinds?.length ?? 0) > 0)
      form.append(
        picker("Compare", ["", ...selectedEntry.compareKinds!], draftValue.compare, (v) => {
          draftValue.compare = v;
          if (selectedEntry.selectedCategoryTemplate) draw();
        }),
      );
    if (selectedEntry.selectedCategoryTemplate && selectedEntry.categoryOptionsPath) {
      const label = node("label", "Category"),
        select = node("select");
      select.append(new Option("All categories", "all"));
      for (const value of categoryValues)
        select.append(new Option(value, JSON.stringify({ value })));
      if (
        typeof draftValue.category === "string" &&
        !categoryValues.includes(draftValue.category)
      ) {
        const missing = new Option(
          draftValue.category,
          JSON.stringify({ value: draftValue.category }),
        );
        missing.disabled = true;
        select.append(missing);
      }
      select.value =
        draftValue.category === null || draftValue.category === undefined
          ? "all"
          : JSON.stringify({ value: draftValue.category });
      select.onchange = () => {
        draftValue.category =
          select.value === "all" ? null : (JSON.parse(select.value) as { value: string }).value;
        draw();
      };
      label.append(select);
      form.append(label);
      if (categoryLoading) form.append(node("span", "Loading categories."));
      if (categoryError) form.append(node("span", categoryError));
    }
    if ((selectedEntry.chartBases?.length ?? 0) > 1 && !selectedEntry.id.includes("item"))
      form.append(
        picker("Chart by", selectedEntry.chartBases!, draftValue.chartBasis, (v) => {
          draftValue.chartBasis = v;
        }),
      );
    if (selectedEntry.chartMetrics?.length)
      form.append(
        picker("Chart metric", selectedEntry.chartMetrics, draftValue.chartMetric, (v) => {
          draftValue.chartMetric = v;
        }),
      );
    if (selectedEntry.requiresCountSession || selectedEntry.requiresTwoCountSessions) {
      form.append(button("Load closed count sessions", () => void loadSessions(), busy));
      if (sessions.length) {
        for (const [key, title] of (selectedEntry.requiresTwoCountSessions
          ? [
              ["baselineCountSessionId", "Baseline"],
              ["comparisonCountSessionId", "Comparison"],
            ]
          : [["countSessionId", "Closed session"]]) as Array<
          ["baselineCountSessionId" | "comparisonCountSessionId" | "countSessionId", string]
        >) {
          const label = node("label", title),
            select = node("select");
          select.append(new Option("Choose session", ""));
          for (const session of sessions) select.append(new Option(session.label, session.id));
          select.value = draftValue[key];
          select.onchange = () => {
            draftValue[key] = select.value;
          };
          label.append(select);
          form.append(label);
        }
      }
    }
    const namedCategoryUnavailable =
      typeof draftValue.category === "string" &&
      (categoryLoading || categoryError !== "" || !categoryValues.includes(draftValue.category));
    const apply = button("Apply", () => {}, busy || namedCategoryUnavailable);
    apply.type = "submit";
    form.append(
      apply,
      button(
        "Cancel",
        () => {
          draft = undefined;
          categorySequence++;
          categoryRequestKey = "";
          editing = false;
          error = "";
          draw();
        },
        busy,
      ),
    );
    form.onsubmit = (event) => {
      event.preventDefault();
      if (busy || namedCategoryUnavailable) return;
      try {
        cannedRunBody(selectedEntry, draftValue, catalog!);
        applied = structuredClone(draftValue);
        draft = undefined;
        editing = false;
        saved = undefined;
        requestId = undefined;
        result = undefined;
        ranBody = undefined;
        void run();
      } catch (cause) {
        error = cause instanceof Error ? cause.message : "Choose valid report filters.";
        draw();
      }
    };
    return form;
  }
  async function refresh() {
    const captured = ++generation;
    busy = true;
    error = "";
    catalog = undefined;
    result = undefined;
    draw();
    try {
      const reply = await host.request<unknown>(
        "GET",
        `${host.prefix}/catalog${host.locationId ? `?locationId=${encodeURIComponent(host.locationId)}` : ""}`,
      );
      if (!active() || captured !== generation) return;
      if (reply.status !== 200 || !isCannedCatalog(reply.body)) {
        error = message(reply);
        return;
      }
      catalog = reply.body;
      busy = false;
      const initial =
        catalog.reports.find((r) => r.id === "sales-summary" && r.canRun) ??
        catalog.reports.find((r) => r.canRun && CANNED_FAMILIES[r.id]) ??
        catalog.reports[0];
      if (initial) choose(initial);
      else {
        entry = undefined;
        error = "No reports are currently available.";
        draw();
      }
    } catch {
      if (active()) error = "Could not load reports.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      } else if (active() && catalog) {
        busy = false;
        draw();
      }
    }
  }
  async function run() {
    if (busy || !catalog || !entry || !applied) return;
    let body: Record<string, unknown>;
    try {
      body = cannedRunBody(entry, applied, catalog);
    } catch (cause) {
      error = cause instanceof Error ? cause.message : "Choose report filters.";
      draw();
      return;
    }
    const captured = ++generation;
    busy = true;
    error = "";
    result = undefined;
    ranBody = undefined;
    saved = undefined;
    requestId = undefined;
    draw();
    try {
      const reply = await host.request<unknown>(
        "POST",
        `${host.prefix}/${CANNED_FAMILIES[entry.id]!.route}/run`,
        body,
      );
      if (!active() || captured !== generation) return;
      if (
        reply.status !== 200 ||
        !isCannedResult(reply.body) ||
        reply.body.template.id !== CANNED_FAMILIES[entry.id]!.origin ||
        (record(body["categories"]) && body["categories"]["kind"] === "named"
          ? reply.body.template.hash !== body["templateHash"] ||
            reply.body.template.version !== body["templateVersion"]
          : reply.body.template.hash !== entry.template?.hash)
      ) {
        error = message(reply);
        return;
      }
      result = reply.body;
      ranBody = body;
    } catch {
      if (active()) error = "Could not load this report.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      }
    }
  }
  async function loadSessions() {
    if (busy || !catalog || !draft) return;
    const summary = catalog.reports.find(
      (r) => r.id === "closed-count-session-summary" && r.canRun,
    );
    if (!summary) {
      error = "Closed count-session selection is unavailable.";
      draw();
      return;
    }
    const captured = generation;
    busy = true;
    error = "";
    draw();
    try {
      const body = cannedRunBody(summary, { ...draft, chartBasis: "date" }, catalog),
        reply = await host.request<unknown>(
          "POST",
          `${host.prefix}/closed-count-session-summary/run`,
          body,
        );
      if (!active() || captured !== generation) return;
      if (reply.status !== 200 || !isCannedResult(reply.body)) {
        error = message(reply);
        return;
      }
      if (reply.body.display.truncated) {
        error = "Choose a smaller date range to select from the complete session list.";
        return;
      }
      sessions = reply.body.rows
        .filter((row) => typeof row["sessionId"] === "string")
        .map((row) => ({
          id: String(row["sessionId"]),
          label: `${String(row["businessDate"])} · ${String(row["locationName"])} · ${String(row["sessionId"])}`,
        }));
      if (!sessions.length) error = "No recorded closed sessions in this period.";
    } catch {
      error = "Could not load closed sessions.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      }
    }
  }
  async function download() {
    if (busy || !entry || !ranBody) return;
    busy = true;
    error = "";
    const captured = generation;
    draw();
    try {
      const reply = await host.file(
        `${host.prefix}/${CANNED_FAMILIES[entry.id]!.route}/export?format=csv`,
        ranBody,
      );
      if (!active() || captured !== generation) return;
      if (reply.status === 200 && reply.blob) saveBlob(reply.blob, reply.filename ?? "report.csv");
      else {
        error = reply.message ?? "Could not download this report.";
        if (reply.status === 401 || reply.status === 403) {
          result = undefined;
          ranBody = undefined;
        }
      }
    } catch {
      error = "Could not download this report.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      }
    }
  }
  async function save(schedule: boolean) {
    if (busy || editing || !entry || !ranBody || !result) return;
    const captured = generation;
    busy = true;
    error = "";
    draw();
    try {
      if (!saved) {
        requestId ??= crypto.randomUUID();
        const reply = await host.request<unknown>(
          "POST",
          `${host.prefix}/${CANNED_FAMILIES[entry.id]!.route}/save`,
          { ...ranBody, requestId, title: saveTitle.trim() || entry.title },
        );
        if (!active() || captured !== generation) return;
        if (
          reply.status !== 200 ||
          !record(reply.body) ||
          typeof reply.body["id"] !== "string" ||
          !Number.isSafeInteger(reply.body["currentVersion"])
        ) {
          error = message(reply);
          return;
        }
        saved = { id: reply.body["id"], version: Number(reply.body["currentVersion"]) };
      }
      if (schedule && saved) {
        busy = false;
        draw();
        mountReportScheduleDialog(
          root,
          scheduleHost(host, () => active() && captured === generation, saved.id),
          {
            definitionId: saved.id,
            version: saved.version,
            ...(cannedLinkOnly(result.template) ? { linkOnly: true } : {}),
          },
        );
      }
    } catch {
      error = "Could not save this report. Retry preserves the save request.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        if (!schedule || error) draw();
      }
    }
  }
  void refresh();
  return {
    destroy() {
      alive = false;
      generation++;
      hideHoverHelp();
      root.remove();
    },
    refresh,
  };
}
function chartAvailable(result: CannedResultDto): boolean {
  if (cannedFamily(result.template) === "received-spend-by-vendor")
    return (
      record(result["chart"]) &&
      result["chart"]["availability"] === "ready" &&
      result["chart"]["truncated"] === false &&
      Array.isArray(result["chart"]["points"]) &&
      result["chart"]["points"].length > 0
    );
  return (
    record(result["chart"]) &&
    ((Array.isArray(result["chart"]["points"]) && result["chart"]["basis"] !== "none") ||
      Array.isArray(result["chart"]["panels"]))
  );
}

/** Opens only an actual stored selected version. The server revalidates the origin/hash and all current read/export rights. */
export function mountCannedSavedReport(
  target: HTMLElement,
  host: CannedReportHost,
  input: {
    definitionId: string;
    version: number;
    origin: CannedOriginDto;
    title: string;
    versions?: Array<{ versionNumber: number }>;
    openVersion?: (version: number) => void;
    back: () => void;
  },
): CannedFrame {
  let alive = true,
    generation = 0,
    busy = false,
    error = "",
    mode: "table" | "chart" = "table",
    result: CannedResultDto | undefined;
  const root = node("section");
  root.className = "canned-saved";
  target.replaceChildren(root);
  const active = () => alive && host.active() && root.isConnected;
  const draw = () => {
    if (!active()) return;
    root.replaceChildren();
    const actions = node("div");
    actions.className = "canned-actions";
    actions.append(
      button("Reports", input.back, busy),
      node("h2", `${input.title} · v${input.version}`),
    );
    if (input.versions?.length) {
      const select = node("select");
      select.setAttribute("aria-label", "Saved report version");
      for (const version of input.versions)
        select.append(
          new Option(`Version ${version.versionNumber}`, String(version.versionNumber)),
        );
      select.value = String(input.version);
      select.disabled = busy;
      select.onchange = () => {
        if (input.openVersion) input.openVersion(Number(select.value));
        else void changeVersion(Number(select.value));
      };
      actions.append(select);
    }
    if (host.setAudience)
      actions.append(
        button(
          "Sharing",
          () => {
            const captured = generation;
            void shareCannedReport(
              root,
              host,
              input.definitionId,
              () => active() && generation === captured,
              draw,
            );
          },
          busy,
        ),
      );
    actions.append(
      button("Run", () => void refresh(), busy),
      button(
        "Table",
        () => {
          mode = "table";
          draw();
        },
        busy || !result,
      ),
      button(
        "Chart",
        () => {
          mode = "chart";
          draw();
        },
        busy || !result || !chartAvailable(result),
      ),
      button("CSV", () => void download(), busy || !result),
    );
    root.append(actions);
    if (error) {
      const text = node("p", error);
      text.setAttribute("role", "alert");
      root.append(text);
    }
    if (busy) root.append(node("p", "Loading."));
    if (result) {
      root.append(renderCannedResult(result, mode));
      mountReportSchedules(root, scheduleHost(host, active, input.definitionId), {
        definitionId: input.definitionId,
        version: input.version,
        ...(cannedLinkOnly(result.template) ? { linkOnly: true } : {}),
      });
    }
  };
  async function changeVersion(version: number) {
    const captured = ++generation;
    busy = true;
    result = undefined;
    error = "";
    draw();
    try {
      const reply = await host.request<unknown>(
        "GET",
        `${host.customPrefix}/saved/${encodeURIComponent(input.definitionId)}/versions/${version}`,
      );
      if (!active() || captured !== generation) return;
      if (
        reply.status !== 200 ||
        !record(reply.body) ||
        !originDto(reply.body["cannedOrigin"]) ||
        !cannedFamily(reply.body["cannedOrigin"])
      ) {
        error = message(reply);
        return;
      }
      input = {
        ...input,
        version,
        origin: reply.body["cannedOrigin"],
        title: typeof reply.body["title"] === "string" ? reply.body["title"] : input.title,
      };
      busy = false;
      await refresh();
    } catch {
      if (active()) error = "Could not load this version.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      }
    }
  }
  async function refresh() {
    if (busy) return;
    const captured = ++generation;
    busy = true;
    result = undefined;
    error = "";
    draw();
    try {
      const reply = await host.request<unknown>(
        "POST",
        `${host.prefix}/saved/${encodeURIComponent(input.definitionId)}/run`,
        { version: input.version },
      );
      if (!active() || captured !== generation) return;
      if (
        reply.status !== 200 ||
        !isCannedResult(reply.body) ||
        reply.body.template.id !== input.origin.id ||
        reply.body.template.version !== input.origin.version ||
        reply.body.template.hash !== input.origin.hash
      ) {
        error = message(reply);
        return;
      }
      result = reply.body;
    } catch {
      if (active()) error = "Could not run this report.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      }
    }
  }
  async function download() {
    if (busy || !result) return;
    const captured = generation;
    busy = true;
    error = "";
    draw();
    try {
      const reply = await host.file(
        `${host.prefix}/saved/${encodeURIComponent(input.definitionId)}/export?format=csv`,
        { version: input.version },
      );
      if (!active() || captured !== generation) return;
      if (reply.status === 200 && reply.blob) saveBlob(reply.blob, reply.filename ?? "report.csv");
      else {
        error = reply.message ?? "Could not download this report.";
        if (reply.status === 401 || reply.status === 403) result = undefined;
      }
    } catch {
      error = "Could not download this report.";
    } finally {
      if (active() && captured === generation) {
        busy = false;
        draw();
      }
    }
  }
  void refresh();
  return {
    destroy() {
      alive = false;
      generation++;
      root.remove();
    },
    refresh,
  };
}
// RPTLAYOUT1-F1-S1: the Reports experience shares the frame's own reply wording and its schedule host.
export { scheduleHost, message as cannedReplyMessage };
